import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../ui/Layout';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { Invoice } from '../../domain/entities/Invoice';
import { Recordatorio } from '../../domain/entities/Recordatorio';
import './PaymentConfirmationPage.css';
import KRGlue from '@lyracom/embedded-form-glue';
import { ServiceProvider } from '../../application/services/ServiceProvider';

const PaymentConfirmationPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as {
    invoice?: Invoice;
    recordatorio?: Recordatorio;
    paymentAmount: number;
    isPartialPayment: boolean;
    tipo?: string;
  };
  const { paymentAmount, isPartialPayment } = state;
  const isRecordatorio = state.tipo === 'RECORDATORIO';
  const invoice = state.invoice as Invoice;
  const recordatorio = state.recordatorio as Recordatorio;

  
  const formError = null;
  const formLoading = false;
  
  // Ir a la página anterior
  const handleGoBack = () => {
    navigate(-1);
  };
  

  const paymentFormService = useMemo(() => ServiceProvider.getPaymentFormService(), []);
  const paymentService = useMemo(() => ServiceProvider.getPaymentService(), []);
  const recordatorioFormService = useMemo(() => ServiceProvider.getRecordatorioFormService(), []);
  const recordatorioService = useMemo(() => ServiceProvider.getRecordatorioService(), []);

  const paymentIdRef = useRef<number | null>(null);
  const krRef = useRef<any>(null);

  useEffect(() => {
    let cancelled = false;

    async function setupPaymentForm() {
      let formToken: string;

      if (isRecordatorio) {
        formToken = await recordatorioFormService.getFormToken(recordatorio, paymentAmount);
      } else {
        const total = invoice.total;
        invoice.total = paymentAmount;
        formToken = await paymentFormService.getFormToken(invoice);
        invoice.total = total;
      }

      if (cancelled) return;

      // Obtener instancia KR fresca en cada montaje para evitar estado obsoleto
      const { KR } = await KRGlue.loadLibrary(
        process.env.REACT_APP_PAYZEN_API_URL || "",
        process.env.REACT_APP_PAYZEN_PUBLIC_KEY || ""
      );
      krRef.current = KR;

      if (cancelled) return;

      await KR.setFormConfig({
        formToken: formToken,
        'kr-language': 'es-CO',
        smartForm: {
          paymentMethods: {
            ALL: {
              displayPaymentReceipt: true
            }
          } as any,
          groupingThreshold: 0,
          hidden: false
        }
      })

      await KR.renderElements(
        '#myPaymentForm'
      )

      // Registrar el pago inicialmente como PENDIENTE
      try {
        let initialResponse: { id: number };
        if (isRecordatorio) {
          initialResponse = await recordatorioService.registerPayment({
            uuid: 'PENDIENTE',
            nroRecordatorio: recordatorio.nroRecordatorio,
            valorPagado: paymentAmount,
            identificacion: recordatorio.nitCliente,
            estado: 'PENDIENTE',
            fechaPago: new Date().toISOString(),
            medioPago: 'PSE',
            nombreCliente: recordatorio.nombreFideicomiso,
            codigoFideicomiso: Number(recordatorio.codigoFideicomiso) as any,
            oficina: recordatorio.idSucursal ?? undefined,
            intereses: -(recordatorio.valorTotalMora),
            detalleFactura: recordatorio.detalle,
            fechaFactura: recordatorio.fechaRegistro,
            valorTotal: recordatorio.saldoTotalAdeudado + recordatorio.valorTotalMora,
            valorSaldo: recordatorio.saldoTotalAdeudado
          });
        } else {
          initialResponse = await paymentService.registerPayment({
            uuid: 'PENDIENTE',
            valorPagado: paymentAmount,
            identificacion: invoice.clientId.toString(),
            estado: 'PENDIENTE',
            fechaPago: new Date().toISOString(),
            medioPago: 'PSE',
            factura: invoice.invoiceNumber,
            codigoFideicomiso: invoice.fideicomiso,
            oficina: invoice.office,
            prefijo: invoice.prefijo,
            numeroFactura: invoice.numeroFactura,
            nombre: invoice.clientName,
            detalleFactura: invoice.description,
            fechaVencimiento: invoice.dueDate,
            intereses: invoice.paymentInterest,
            fechaFactura: invoice.issueDate,
            valorTotal: invoice.totalAmount,
            secuencial: parseInt(invoice.secuencialDetalle),
            valorSaldo: invoice.total
          });
        }
        paymentIdRef.current = initialResponse.id;
      } catch (error) {
        console.error('Error al registrar el pago inicial:', error);
      }

      await KR.onOrderUpdate(async paymentData => {
        const paymentData_any = paymentData as any;
        const paymentMethodUsed = paymentData_any.rawClientAnswer
          ? JSON.parse(paymentData_any.rawClientAnswer)?.transactions?.[0]?.paymentMethodType
          : 'desconocido';
        const uuid = paymentData_any.rawClientAnswer
          ? JSON.parse(paymentData_any.rawClientAnswer)?.transactions?.[0]?.uuid
          : 'desconocido';
        const orderId = paymentData.clientAnswer.orderDetails.orderId;

        // Validar que el orderId corresponde al documento actual
        const orderIdEsperado = isRecordatorio
          ? `REC-${recordatorio.nroRecordatorio}`
          : `${invoice.prefijo}-${invoice.numeroFactura}`;

        if (orderId !== orderIdEsperado) {
          console.log('OrderId no coincide. Recibido:', orderId, 'Esperado:', orderIdEsperado);
          return false;
        }

        let estado = '';
        let redirectPath = '';

        if (paymentData.clientAnswer.orderStatus === 'PAID' ||
            paymentData.clientAnswer.orderStatus === 'AUTHORISED' ||
            paymentData.clientAnswer.orderStatus === 'CAPTURED') {
          estado = 'APROBADO';
          redirectPath = '/payment/success';
        } else if (paymentData.clientAnswer.orderStatus === 'PENDING' ||
                   paymentData.clientAnswer.orderStatus === 'WAITING' ||
                   paymentData.clientAnswer.orderStatus === 'RUNNING') {
          estado = 'PENDIENTE';
          redirectPath = '/payment/pending';
        } else {
          estado = 'RECHAZADO';
          redirectPath = '/payment/failed';
        }

        const medioPago = paymentMethodUsed === 'CARD' ? 'TARJETA_CREDITO' : paymentMethodUsed;

        try {
          if (isRecordatorio) {
            await recordatorioService.registerPayment({
              id: paymentIdRef.current || undefined,
              uuid,
              nroRecordatorio: recordatorio.nroRecordatorio,
              valorPagado: paymentAmount,
              identificacion: recordatorio.nitCliente,
              estado,
              fechaPago: new Date().toISOString(),
              medioPago,
              nombreCliente: recordatorio.nombreCliente
            });
          } else {
            await paymentService.registerPayment({
              id: paymentIdRef.current || undefined,
              uuid,
              valorPagado: paymentAmount,
              identificacion: invoice.clientId.toString(),
              estado,
              fechaPago: new Date().toISOString(),
              medioPago,
              factura: invoice.invoiceNumber,
              codigoFideicomiso: invoice.fideicomiso,
              oficina: invoice.office,
              prefijo: invoice.prefijo,
              numeroFactura: invoice.numeroFactura,
              nombre: invoice.clientName,
              detalleFactura: invoice.description,
              fechaVencimiento: invoice.dueDate,
              intereses: invoice.paymentInterest,
              fechaFactura: invoice.issueDate,
              valorTotal: invoice.totalAmount,
              secuencial: parseInt(invoice.secuencialDetalle),
              valorSaldo: invoice.total
            });
          }

          const navState = isRecordatorio
            ? { recordatorio, paymentAmount, isPartialPayment, paymentMethodUsed, tipo: 'RECORDATORIO',
                ...(redirectPath === '/payment/pending' && { uuid }) }
            : { invoice, paymentAmount, isPartialPayment, paymentMethodUsed,
                ...(redirectPath === '/payment/pending' && { uuid }) };

          navigate(redirectPath, { state: navState });
        } catch (error) {
          console.error('Error al registrar el pago:', error);
          const navStateErr = isRecordatorio
            ? { recordatorio, paymentAmount, isPartialPayment, paymentMethodUsed, tipo: 'RECORDATORIO',
                error: 'Error al registrar el pago en el sistema',
                ...(redirectPath === '/payment/pending' && { uuid }) }
            : { invoice, paymentAmount, isPartialPayment, paymentMethodUsed,
                error: 'Error al registrar el pago en el sistema',
                ...(redirectPath === '/payment/pending' && { uuid }) };
          navigate(redirectPath, { state: navStateErr });
        }

        return true;
      });
      
    }
    setupPaymentForm();
    return () => {
      cancelled = true;
      krRef.current?.removeForms();
    };
  }, [invoice, recordatorio, isRecordatorio, paymentAmount, isPartialPayment, navigate, paymentFormService, paymentService, recordatorioFormService, recordatorioService]);
  
  // Formatear números como moneda
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(value);
  };

  if (formLoading) {
    return (
      <Layout>
        <Card title="Confirmación de Pago">
          <div className="loading-container">
            <p>Cargando formulario de pago...</p>
          </div>
        </Card>
      </Layout>
    );
  }

  if (formError) {
    return (
      <Layout>
        <Card title="Confirmación de Pago">
          <div className="error-container">
            <p>Error al cargar el formulario de pago: {formError}</p>
            <Button onClick={handleGoBack}>Volver</Button>
          </div>
        </Card>
      </Layout>
    );
  }

  return (
    <Layout>
      <Card title="Confirmación de Pago">
        <div className="payment-confirmation">
          <div className="summary-section">
            <h3>Resumen de Pago</h3>

            <div className="summary-row">
              <span className="label">{isRecordatorio ? 'Recordatorio de Pago:' : 'Factura:'}</span>
              <span className="value">
                {isRecordatorio ? `RDP${recordatorio.nroRecordatorio}` : invoice.invoiceNumber}
              </span>
            </div>

            <div className="summary-row">
              <span className="label">Cliente:</span>
              <span className="value">
                {isRecordatorio ? recordatorio.nombreCliente : invoice.clientName}
              </span>
            </div>

            <div className="summary-row">
              <span className="label">Valor Original:</span>
              <span className="value">
                {isRecordatorio
                  ? formatCurrency(recordatorio.saldoTotalAdeudado + recordatorio.valorTotalMora)
                  : formatCurrency(invoice.totalAmount)}
              </span>
            </div>


            <div className="summary-row">
              <span className="label">Valor a Pagar:</span>
              <span className="value highlight">{formatCurrency(paymentAmount)}</span>
            </div>

            {isPartialPayment && (
              <div className="partial-payment-info">
                <p>El pago parcial será aplicado primero a intereses y el restante a capital</p>
              </div>
            )}
          </div>
          
          <div className="payment-method-section">
            <div id="myPaymentForm">
              <div 
                className="kr-smart-form"
              ></div>
            </div>
          </div>
        </div>
      </Card>
    </Layout>
  );
};

export default PaymentConfirmationPage; 